/**
 * Descout Horizon Mark — the brand logo.
 *
 * A minimal mark: amber sun dot above a white horizon line,
 * optionally followed by the "descout" wordmark.
 *
 * Variants:
 * - "full"    → mark + wordmark (nav, footer)
 * - "mark"    → mark only (favicon-style, compact)
 * - "loader"  → animated sun-rise version
 */

type HorizonMarkProps = {
  variant?: 'full' | 'mark' | 'loader';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
};

const sizes = {
  sm: { sun: 'w-[6px] h-[6px]', line: 'w-[14px] h-[1px]', text: 'text-sm', gap: 'gap-[6px]', markGap: 'gap-0' },
  md: { sun: 'w-[8px] h-[8px]', line: 'w-[18px] h-[1.5px]', text: 'text-base', gap: 'gap-2', markGap: 'gap-0' },
  lg: { sun: 'w-[10px] h-[10px]', line: 'w-[22px] h-[1.5px]', text: 'text-2xl', gap: 'gap-[10px]', markGap: 'gap-0' },
};

export default function HorizonMark({ variant = 'full', size = 'md', className = '' }: HorizonMarkProps) {
  const s = sizes[size];

  if (variant === 'loader') {
    return (
      <div
        role="status"
        aria-label="Loading"
        className={`relative w-16 h-16 flex items-center justify-center ${className}`}
      >
        <div className={`${s.sun} rounded-full bg-scout-gold absolute animate-sun-rise`} />
        <div className={`${s.line} bg-parchment absolute bottom-[18px] left-1/2 -translate-x-1/2 animate-line-grow`} />
        <span className="sr-only">Loading…</span>
      </div>
    );
  }

  const mark = (
    <div className={`flex flex-col items-start ${s.markGap}`}>
      <div className={`${s.sun} rounded-full bg-scout-gold ml-[1px] mb-[3px]`} />
      <div className={`${s.line} bg-parchment dark:bg-parchment`} />
    </div>
  );

  if (variant === 'mark') {
    return <div className={className}>{mark}</div>;
  }

  // Full variant: mark + wordmark
  return (
    <div className={`flex items-center ${s.gap} ${className}`}>
      {mark}
      <span className={`${s.text} font-bold tracking-[-0.02em] text-slate-900 dark:text-parchment font-display`}>
        descout
      </span>
    </div>
  );
}

/**
 * StateMark — the horizon mark sized for empty / loading / error states.
 *
 * Brand-neutral replacement for the retired ERK mascot. Decorative by default
 * (aria-hidden) since the empty-state heading carries the meaning. Proportions
 * (sun ≈ 26% of the box, line ≈ 58%, airspace between) match the design-system
 * construction in guidelines/brand-mark.html.
 *
 * - `animated` → delegates to the tuned 64px <HorizonMark variant="loader" />.
 * - static    → renders the mark at any `size` (px), centered in its box.
 */
export function StateMark({
  animated = false,
  size = 72,
  className = '',
}: {
  animated?: boolean;
  size?: number;
  className?: string;
}) {
  if (animated) {
    return <HorizonMark variant="loader" className={className} />;
  }

  const sun = Math.round(size * 0.26);
  const lineWidth = Math.round(size * 0.58);
  const lineHeight = Math.max(2, Math.round(size * 0.04));

  return (
    <div
      aria-hidden="true"
      className={`relative inline-block ${className}`}
      style={{ width: size, height: size }}
    >
      <div
        className="absolute rounded-full bg-scout-gold left-1/2 -translate-x-1/2"
        style={{ width: sun, height: sun, top: '20%' }}
      />
      <div
        className="absolute bg-parchment left-1/2 -translate-x-1/2 rounded-[1px]"
        style={{ width: lineWidth, height: lineHeight, bottom: '22%' }}
      />
    </div>
  );
}
